<template>
  <div v-query-params="tab" class="tabs">
    <div class="tabs__tabs">
      <button
        v-for="{ option, index } in visibleTabOptions"
        :key="index"
        :data-cy="option?.dataCy ?? ''"
        class="tabs__tab"
        :class="{
          'tabs__tab--active': isTabActive(index),
          'tabs__tab--first': isFirstTab(index),
          'tabs__tab--last': isLastTab(index),
          hide: option.hideTab,
        }"
        @click="setActiveTab(index)"
      >
        {{ option.display }}
      </button>

      <div ref="menuRef" class="tabs__menu-container">
        <button
          v-if="showMore"
          class="tabs__tab tabs__tab--last"
          @click="toggleMenu"
        >
          {{ $t('MORE') }}

          <ChevronIcon class="tabs__chevron-icon" direction="down" />
        </button>
        <div v-if="isMenuVisible" class="tabs__menu">
          <button
            v-for="(option, index) in menuTabOptions"
            :key="index"
            class="tabs__menu-item"
            :class="{
              'tabs__menu-item--active': isTabActive(index),
              hide: option.hideTab,
            }"
            @click="setActiveTab(index)"
          >
            {{ option.display }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="displayTabContent"
      :class="[
        'tabs__content',
        { 'tabs__content--no-box-container': noBoxContainer },
      ]"
    >
      <slot :active-tab="activeTabOption.value"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import ChevronIcon from '@/components/icon_components/ChevronIcon.vue'
import { useTabOptions } from '@/hooks/useTabOptions'
import { onClickOutside } from '@vueuse/core'
import { computed, ref, toRefs } from 'vue'

interface TabOptions {
  display: string
  value: any
  dataCy?: string
  hideTab?: boolean
}

interface SetActiveTabEvent {
  index: number
  value: any
}

interface Props {
  maxTabs?: number
  options: TabOptions[]
  noBoxContainer?: boolean
  displayTabContent?: boolean
  keepTabs?: boolean
  initialTabIndex?: number
}

const props = withDefaults(defineProps<Props>(), {
  maxTabs: 3,
  noBoxContainer: false,
  displayTabContent: true,
  keepTabs: true,
  initialTabIndex: 0,
})

const emit = defineEmits<{
  onSetActiveTab: [event: SetActiveTabEvent]
}>()

const {
  activeTabOption,
  tab,
  isTabActive,
  setActiveTab: setActiveTabInternal,
  activeTabIndex,
} = useTabOptions(toRefs(props))

const isMenuVisible = ref(false)
const menuRef = ref<HTMLElement | null>(null)
onClickOutside(menuRef, () => {
  isMenuVisible.value = false
})

const visibleTabOptions = computed(() => {
  const sliceAt =
    activeTabIndex.value < props.maxTabs ? props.maxTabs : props.maxTabs - 1

  const visibleTabs = props.options
    .slice(0, sliceAt)
    .filter((option) => !option.hideTab)
    .map((option, index) => ({ option, index }))

  if (sliceAt !== props.maxTabs) {
    visibleTabs.push({
      option: activeTabOption.value,
      index: activeTabIndex.value,
    })
  }
  return visibleTabs
})

const menuTabOptions = computed<TabOptions[]>(() => {
  return props.options.filter((option) => !option.hideTab)
})

const showMore = computed(() => {
  return props.options.length > props.maxTabs
})

const isFirstTab = (index: number): boolean => {
  return index === 0
}

const isLastTab = (index: number): boolean => {
  return index === visibleTabOptions.value.length - 1 && !showMore.value
}

const toggleMenu = (menuVisible?: boolean): void => {
  if (typeof menuVisible !== 'boolean') {
    menuVisible = !isMenuVisible.value
  }
  isMenuVisible.value = menuVisible
}

const setActiveTab = (index: number): void => {
  setActiveTabInternal(index)
  toggleMenu(false)

  emit('onSetActiveTab', {
    index,
    value: props.options[index].value,
  })
}
</script>

<style lang="scss" scoped>
.hide {
  display: none;
}

.tabs {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;

  &__tabs {
    display: flex;
  }

  &__tab {
    color: $color-text-light;
    background-color: $color-canvas;
    padding: $spacing-3xs $spacing-xs;
    text-transform: capitalize;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $spacing-5xs;
    overflow: hidden;
    align-self: stretch;

    border: 1px solid $color-border;
    border-bottom: none;
    border-left: none;

    &--first {
      border-left: 1px solid $color-border;
      border-top-left-radius: $border-radius-m;
    }

    &--last {
      border-top-right-radius: $border-radius-m;
    }

    &--active {
      color: $color-canvas;
      font-weight: 600;
      background-color: $color-navigation;
      pointer-events: none;
      cursor: default;
    }
  }

  &__chevron-icon {
    align-self: center;
    width: $spacing-3xs;
    height: $spacing-3xs;
    color: $color-text-light;
  }

  &__menu-container {
    position: relative;
    display: flex;
  }

  &__menu {
    position: absolute;
    top: 100%;
    border: 1px solid $color-border;
    display: grid;
    z-index: $dropdown-list--z-index;
    left: 0;
    max-height: 70vh;
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 10;
    box-shadow: 0px 30px 40px -10px #dde2e4;

    @media (max-width: 1023px) {
      left: unset;
      right: 0;
    }
  }

  &__menu-item {
    border: none;
    background-color: $color-canvas;
    padding: $spacing-2xs $spacing-m;
    cursor: pointer;
    color: $color-text;

    &:hover {
      background-color: $color-marble;
    }
    &--active {
      background-color: $color-disabled;
      pointer-events: none;
      cursor: default;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    background-color: $color-canvas;
    border: 1px solid $color-border;
    border-radius: $border-radius-xl;
    border-top-left-radius: 0;
  }
  &__content--no-box-container {
    background: none;
    border: none;
    border-top: 1px solid $color-border;
    border-radius: 0;
  }
}
</style>
